<template>
  <div class="showcase-profile">
    <spinner v-if="!loaded" />

    <template v-else>
      <div class="top">
        <align small-gap>
          <span v-if="numLikes">{{ $filters.number(numLikes) }}</span>

          <icon-button
            v-if="!isMyCard"
            :tooltip="hasFavorite ? 'Remove favorite' : 'Add to favorites'"
            :icon="hasFavorite ? 'favorite' : 'favorite_border'"
            @click="handleFavorite"
          />

          <icon-button
            tooltip="Close card"
            icon="close"
            @click="close"
          />
        </align>
      </div>

      <section class="details">
        <card-profile-images :card="card" />

        <div class="stats">
          <div class="main">
            <div class="card-text">
              {{ card.year }} {{ card.set }} {{ card.player }}{{ card.number ? ` #${card.number}` : '' }}
            </div>

            <card-item-chips
              profile
              :card="card"
            />
          </div>

          <div class="stat-items">
            <owner-stat-item :owner="card" />

            <stat-item
              label="Date Added"
              :value="$filters.date(card.dateAdded)"
            />
          </div>

          <div class="button-row">
            <btn
              v-if="!!profileLink"
              icon="question_answer"
              as="a"
              target="_blank"
              :href="profileLink"
            >
              Message Owner
            </btn>

            <btn
              v-if="!!cardId"
              icon="cl_ladder"
              kind="secondary"
              @click="goToLadderCard"
            >
              View Ladder Stats
            </btn>
          </div>
        </div>
      </section>

      <showcase-card-more-cards :card="card" :cards="moreCards" />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { authService, searchService } from '@/services';

import CardItemChips from '@cardladder/shared/components/CardItemChips';
import CardProfileImages from '@cardladder/shared/components/CardProfileImages';
import OwnerStatItem from '@cardladder/shared/components/OwnerStatItem';
import ShowcaseCardMoreCards from '@cardladder/shared/components/ShowcaseCardMoreCards';
import StatItem from '@cardladder/shared/components/StatItem';

export default {
  components: {
    CardItemChips,
    CardProfileImages,
    OwnerStatItem,
    ShowcaseCardMoreCards,
    StatItem,
  },

  props: {
    id: String,
  },

  data() {
    return {
      card: {},
      cardId: null,
      loaded: false,
      moreCards: [],
      hasFavorite: false,
      numLikes: 0,
    };
  },

  created() {
    this.load();
  },

  watch: {
    id(to, from) {
      if (to !== from) {
        this.load();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    isMyCard() {
      return this.user?.uid === this.card?.uid;
    },

    profileLink() {
      if (!this.card || !this.card.profileLink) {
        return '';
      }

      if (!this.card.profileLink.includes('http') && this.card.profileLink.includes('@')) {
        return `https://instagram.com/${this.card.profileLink.replace('@', '')}`;
      }

      return this.card.profileLink;
    },
  },

  methods: {
    async load() {
      try {
        this.loaded = false;
        this.card = {};
        this.cardId = null;

        if (!this.id) {
          this.close();
          return;
        }

        const collectionCard = await searchService.getDoc('collectioncards', this.id);

        this.cardId = collectionCard.cardId || null;
        this.card = collectionCard;
        this.hasFavorite = this.card.likes && this.card.likes.includes(this.user.uid);
        this.numLikes = this.card.numLikes || 0;

        const data = await searchService.search({
          index: 'collectioncards',
          limit: 5,
          filters: {
            collectionId: this.card.collectionId,
            public: true,
            hasQuantityAvailable: true,
          },
        });

        this.moreCards = data.hits.filter((card) => card.id !== this.id).slice(0, 4);
      } catch (e) {
        this.close();
      }

      this.loaded = true;
    },

    async handleFavorite() {
      try {
        this.hasFavorite = !this.hasFavorite;
        this.numLikes += this.hasFavorite ? 1 : -1;
        await authService.likeShowcaseCard(this.user.uid, this.card, this.hasFavorite);
        this.$toasted.success(`${this.hasFavorite ? 'Added to' : 'Removed from'} your favorites!`);
      } catch (e) {
        this.$toasted.error('Error adding to your favorites');
        Sentry.captureException(e);
      }
    },

    goToLadderCard() {
      const query = { ...this.$route.query };
      delete query.showcaseCardId;

      this.$router.push({
        ...this.$route,
        query: {
          ...query,
          cardId: this.cardId,
        },
      });
    },

    close() {
      const query = { ...this.$route.query };
      delete query.showcaseCardId;
      this.$router.replace({ query });
    },
  },
};
</script>

<style lang="scss" scoped>
.showcase-profile {
  min-height: 100%;
  position: relative;
  padding: $gp;
}

.top {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $gp * .5
}

.details {
  margin-bottom: $gp * 1.5;
}

.card-text {
  margin-bottom: $gp * .5;
  font-size: 22px;
  font-weight: bold;
}

.stats {
  margin-top: $gp * 1.5;
}

.stat-items {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: $gp;
}
</style>
